<template>
  <!-- 다른 모달(「2차 인증」 창 등) 위에 떠야 하므로 z-index 를 올린다 -->
  <a-modal
    :open="state.visible"
    :footer="null"
    :mask-closable="false"
    :z-index="2000"
    :width="420"
    wrap-class-name="step-up-modal"
    destroy-on-close
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">{{ title }}</div>
    </template>

    <!-- 창 안 여백·취소 줄은 전역 .security-modal(ant-design.scss) -->
    <div class="modal-content security-modal">
      <p class="step-up-desc">{{ description }}</p>
      <a-spin :spinning="loading">
        <otp-step
          v-if="!loading"
          :key="`${state.purpose}-${enrollMode}`"
          :methods="factors.methods"
          :targets="factors.targets"
          :enroll="enrollMode"
          :send="send"
          :verify="verify"
        />
      </a-spin>
      <!-- 취소하면 하던 작업은 하지 않는다(실패 알림 없음, D5) -->
      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent } from 'vue';
import apiInstance from '@/axios';
import {
  enrollVerifyBody,
  errorCode,
  factorsOf,
  saveSessionTokens,
  setMfaToken,
} from '@/utils/mfa';
import { finishStepUp, stepUpState } from '@/utils/stepUp';
import OtpStep from './OtpStep.vue';

const ENROLL_METHODS = ['ALIMTALK_OTP', 'FALLBACK_EMAIL_OTP'];

/**
 * step-up 창(design.md §1-2·§1-3·§2-3). 서버가 MFA_REQUIRED 면 LOGIN, SEND_AUTH_REQUIRED 면 SEND 목적으로
 * 인증번호를 확인하고 새 증표를 저장한다. 원래 요청 재시도는 axios 인터셉터가 한다(utils/mfa.js retryAfterStepUp).
 * 수단 목록은 열 때마다 프로필 `mfa`(GET /auth/profile)로 만든다(D-104).
 * challenge 가 TWO_FACTOR_NOT_ASSIGNED 면 같은 창을 세션 중 등록 모드(A14·A15)로 바꾸고,
 * 등록이 끝나면 받은 증표·새 토큰을 저장한 뒤 통과로 끝낸다(AC-SEND-007c).
 */
// 이 앱은 TS(allowJs)라 defineComponent 로 감싸야 TS 화면이 이 컴포넌트를 받는다
export default defineComponent({
  name: 'StepUpModal',
  components: { OtpStep },
  data() {
    return {
      state: stepUpState,
      loading: false,
      enrollMode: false,
      profileFactors: { methods: [], targets: {} },
      // 창이 열리는 동안 ant 모달이 창 첫머리로 옮기는 포커스를 입력 칸으로 되돌린다(D2)
      restoreFocus: false,
    };
  },
  computed: {
    // 뒤에 깔린 「2차 인증 · 고객 계정」 창과 헷갈리지 않게 "본인 확인"(D5) — 인증번호는 로그인한 본인에게 간다.
    // 수단이 없어 등록하는 경우는 studio·병원·제조사 앱과 같은 "2차 인증 등록"
    title() {
      return this.enrollMode ? '2차 인증 등록' : '본인 확인';
    },
    description() {
      if (this.enrollMode) {
        return '내 계정에 2차 인증 수단이 없습니다. 휴대폰이나 이메일을 등록하면 하던 작업을 이어서 진행합니다.';
      }
      return this.state.purpose === 'SEND'
        ? '메시지 발송·시나리오 저장 같은 작업 전에 본인 확인이 필요합니다. 인증하면 4시간 동안 다시 묻지 않습니다.'
        : '보안이 중요한 작업이라 본인 확인이 한 번 더 필요합니다. 인증하면 하던 작업을 이어서 진행합니다.';
    },
    factors() {
      if (this.enrollMode) return { methods: ENROLL_METHODS, targets: {} };
      // 프로필을 못 읽었거나 수단이 없으면 휴대폰으로 시도 → 서버가 TWO_FACTOR_NOT_ASSIGNED 면 등록 모드
      return this.profileFactors.methods.length
        ? this.profileFactors
        : { methods: ['ALIMTALK_OTP'], targets: {} };
    },
  },
  watch: {
    'state.visible'(visible) {
      if (visible) this.open();
    },
  },
  mounted() {
    // 부트스트랩 모달(회사 정보 창 등) 위에 뜰 때 그 모달의 focus trap 이 입력 칸 포커스를 빼앗지 않게 한다
    window.addEventListener('focusin', this.keepFocus, true);
  },
  beforeUnmount() {
    window.removeEventListener('focusin', this.keepFocus, true);
  },
  methods: {
    keepFocus(event) {
      if (!this.state.visible || !event.target.closest) return;
      const own = !!event.target.closest('.step-up-modal');
      if (own) event.stopPropagation();
      // ant 모달(이 창·같이 열린 뒤 창)은 열림 애니메이션이 끝나면 창 첫머리(tabindex 칸)로 포커스를 옮긴다 — 등록 칸이 이미 보이면 그 칸으로.
      // 이 창의 열림 처리가 마지막이라 그때까지만(그 뒤 Tab 순환은 ant 모달 그대로)
      if (
        !this.restoreFocus ||
        !event.target.matches('.ant-modal > [tabindex]')
      )
        return;
      if (own) this.restoreFocus = false;
      const input = document.querySelector(
        '.step-up-modal .otp-target, .step-up-modal .otp-code'
      );
      if (input) input.focus();
    },
    async open() {
      this.restoreFocus = true;
      this.enrollMode = false;
      this.profileFactors = { methods: [], targets: {} };
      this.loading = true;
      try {
        const { data } = await apiInstance.get('/auth/profile', {
          showGlobalLoading: false,
        });
        const mfa = (data && data.mfa) || {};
        this.profileFactors = factorsOf(mfa);
        // 내 수단이 없으면 처음부터 등록 모드(M14). 단 2차 인증 예외 중이면 challenge 가 증표를 바로 주므로(A5) 그대로 둔다.
        // 프로필을 못 읽으면 휴대폰으로 시도 → TWO_FACTOR_NOT_ASSIGNED 면 등록 모드
        this.enrollMode =
          this.profileFactors.methods.length === 0 && !mfa.exemptUntil;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    done(data) {
      setMfaToken(data.mfaToken);
      finishStepUp(true);
    },
    async send(method, target) {
      if (this.enrollMode) {
        const { data } = await apiInstance.post(
          '/auth/2fa/factors/challenge',
          { method, target },
          { showGlobalLoading: false }
        );
        return data;
      }
      try {
        const { data } = await apiInstance.post(
          '/auth/2fa/step-up/challenge',
          { purpose: this.state.purpose, method },
          { showGlobalLoading: false }
        );
        // 2FA 예외가 유효하면 인증번호 없이 증표를 바로 준다(A5)
        if (data && data.mfaToken) this.done(data);
        return data;
      } catch (error) {
        if (errorCode(error) === 'TWO_FACTOR_NOT_ASSIGNED')
          this.enrollMode = true;
        throw error;
      }
    },
    async verify(method, code) {
      if (this.enrollMode) {
        // A15 응답 {mfa, mfaToken, accessToken, refreshToken}: 새 토큰(mfaReq 다시 계산)과 증표를 함께 바꾼다.
        // SEND 창이면 purpose:'SEND' 로 확인해 증표에 sendAt 까지 받는다(D-124)
        const { data } = await apiInstance.post(
          '/auth/2fa/factors/verify',
          enrollVerifyBody(method, code, this.state.purpose),
          { showGlobalLoading: false }
        );
        saveSessionTokens(data);
        finishStepUp(true);
        return;
      }
      const { data } = await apiInstance.post(
        '/auth/2fa/step-up/verify',
        { purpose: this.state.purpose, method, code },
        { showGlobalLoading: false }
      );
      this.done(data);
    },
    handleCancel() {
      finishStepUp(false);
    },
  },
});
</script>

<style scoped>
.step-up-desc {
  margin: 0 0 16px;
  font-size: 0.875rem;
  color: #333;
  line-height: 1.5;
}
</style>
